import React, { Fragment, useCallback, useEffect, useState } from 'react'
import { useProfile } from 'hooks/useProfile';
import { markLogout } from 'utils/url';
import { useDispatch, useSelector } from 'react-redux';
import { Link, NavLink, useHistory, useLocation, useParams } from 'react-router-dom';
import { Button, Icon } from '@safelyq/bulma-ui-library';
import './styles.css';
import styled from 'styled-components';
import { useMarkasViewed } from '../LoginMenu/hooks';
import { getAvatarName } from 'utils/getAvatarName';
import firestore from '@firebase';
import { useLazyQuery } from '@apollo/client';
import { GET_SAFELYQ_API_OPTIONS, GET_USER_APPOINTMENTS, GET_USER_NOTIFICATIONS, GET_USER_UNREAD_MESSAGES, UPDATE_TEMP_CONVRESATION_MESSAGES } from './requests';
import { addAppointments, getAllMessages, setTinyMceKey, updateGoogleRecaptchaApi, } from 'Redux/AuthSlice/AuthSlice';
import moment from 'moment';
import { APIPaths } from 'services/api-provider';
import { setAllowChatSLB, setSearchRadii } from 'Redux/ConsumerSlice';
import { toast } from 'react-toastify';
import { BsCalendar2Date } from "react-icons/bs";
import { useIsMobile } from 'constants';
import { useGoogleRecaptchaApi, useOrdersNavHook, useTinyMCEKey } from './hooks';
import { GrLogout } from "react-icons/gr";
import { AiOutlineShoppingCart } from "react-icons/ai";
import { MdOutlineManageAccounts } from "react-icons/md"
import { ClipLoader } from 'react-spinners';

// Styled components for profile dropdown
const ProfileDropdown = styled.div`
  position: relative;
  display: inline-block;
`;

const DropdownContent = styled.div`
  display: none;
  position: absolute;
  right: 0;
  top: 100%;
  background: #fff;
  min-width: 18rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  border-radius: 4px;
  z-index: 1000;
  padding: 8px 0;

  ${ProfileDropdown}:hover & {
    display: block;
  }
`;

const DropdownItem = styled.button`
  width: 100%;
  background: none;
  border: none;
  padding: 6px 12px;
  text-align: left;
  font-size: 0.9rem;
  color: #4a4a4a;
  cursor: pointer;
  display: flex;
  align-items: center;
  &:hover {
    background: #f5f5f5;
  }
`;

const OrdersBadge = styled.span`
  display: inline-block;
  background: #993c1d;
  color: #ffffffff;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 2px 12px;
  border-radius: 12px;
`;

const ApplicationPaths = {
  Register: '/account/register',
  Login: '/authentication/login',
  Logout: '/authentication/logout',
  Profile: '/authentication/profile',
};

const NavMenu = () => {
  const { logout, login } = useProfile();
  const { push } = useHistory();

  // When logging out, change the authPersist state to false
  const handleLogout = () => {
    markLogout() // tell the other tabs we logged out
    localStorage.setItem("authPersist", "false")
    logout();
  };

  const { onMarkAsViewed } = useMarkasViewed();
  const { userinfo, authinfo } = useSelector((state) => state?.auth);
  const { activeOrders: activeOrdersCount } = useSelector((state) => state?.consumer);

  const { googleRecaptchaData } = useGoogleRecaptchaApi(); // fetch google api for recaptcha from backend
  const { tinyMceKey } = useTinyMCEKey(); // fetch google api for recaptcha from backend
  const { isActiveOrdersLoading, getOrders } = useOrdersNavHook();
  const { businessId } = useParams();

  const dispatch = useDispatch()
  const [isActive, setActive] = useState(false);

  // const allowChatSLB = useSelector(state => state?.consumer?.allowChatSLB);

  const handleMenuClick = useCallback(
    () => setActive((isActive) => !isActive),
    [setActive]
  );
  const handleMenuClose = useCallback(() => setActive(false), [setActive]);
  const { pathname } = useLocation();

  // This is resposible for the notifications count update
  const [loadChat] = useLazyQuery(GET_USER_UNREAD_MESSAGES, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    onCompleted: data => {
      dispatch(getAllMessages({
        unreadCount: data.getUser.user.conversationsSummary.unreadCount,
        allMessages: data.getUser.user.conversations,
        totalNotifications: data.isProfileComplete.unviewedCount
      }));
    },
    onError: (error) => {
      toast.error(error?.message)
    }
  });

  // update/delete the temp conversation based on the conversation
  const [deleteTempConversation] = useLazyQuery(UPDATE_TEMP_CONVRESATION_MESSAGES, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    onCompleted: ({ updateTempConversationMessages }) => {
      if (updateTempConversationMessages?.isSuccess) {
        localStorage.removeItem("tempConversationId");
        const chatId = updateTempConversationMessages?.conversationId;

        // set the chatId to the params and redirect the route to it so its apis are called
        if (chatId) {
          push(`?chatId=${chatId}`)
        }
      }
    },
    onError: (error) => {
      toast.error(error?.message)
    }
  });

  // if the conversation id exist at the time of renedering this component, than delete its conversation and on Completed remove it from local storage
  useEffect(() => {
    const deleteConversation = async () => {
      const tempConversationId = localStorage.getItem("tempConversationId");

      if (
        typeof tempConversationId === "string" &&
        tempConversationId !== "null" &&
        tempConversationId !== "undefined"
      ) {
        await deleteTempConversation({ variables: { tempConversationId } });
      }
    };

    deleteConversation();
  }, []);

  // Set to the global state and retrieve where ever requried
  const [loadAppointments, { refetch: refetchAppointments }] = useLazyQuery(GET_USER_APPOINTMENTS, {
    variables: { businessId: 0, status: 'ALL', startDate: moment(new Date()).format('YYYY-MM-DD') },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onCompleted: data => {
      dispatch(addAppointments({ allAppointments: data.getCurrentUserAppointments, totalNotifications: data.isProfileComplete.unviewedCount }));
    },
  });

  const [loadNotifications] = useLazyQuery(GET_USER_NOTIFICATIONS, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onCompleted: data => {
      // handle notifications completion
    },
  });

  const [loadApiOptions] = useLazyQuery(GET_SAFELYQ_API_OPTIONS, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onCompleted: data => {
      dispatch(setAllowChatSLB(data?.safelyqApiOptions?.allowChatSLB));
      dispatch(setSearchRadii(data?.safelyqApiOptions?.searchRadii));
    },
  });

  useEffect(() => {

    loadApiOptions();
  }, []); // Empty dependency array to run once on component mount

  const attemptLogin = () => {
    // do not remove the states as we have the data as redux persist
    if (pathname === "/payment") {
      localStorage.setItem("userLoggingInForPayment", "true")
    }
    // because we need tempConversationId to save/delete conversation so we dont clear data
    else if (pathname === "/search-business-ai") {

    } else {
      sessionStorage.clear();
      localStorage.clear();
    }

    login();
  }

  const attemptRegister = useCallback(
    async (e) => {
      e.preventDefault();
      // window.location.href = `${PrefixHolder.prefixHolder.Gateway}${ApplicationPaths.Register}?returnUrl=${window.location.href}`;
      window.location.href = `${APIPaths.Gateway}${ApplicationPaths.Register}`;
    },
    []//[login]
  );

  const handleMarkAsViewed = (e) => {
    e.preventDefault();
    userinfo?.totalNotifications !== 0 && onMarkAsViewed(); // do not call mark as read if the notifications are already 0
    push('/notifications');
  };

  useEffect(() => {

    const unsubscribe = firestore.collection('chat')
      .where('customerId', '==', `${userinfo.id}`)
      .onSnapshot(async querySnapshot => {

        if (!String(pathname).includes('/messages') && !String(pathname).includes('/search-business-ai')) {
          // They both have seperate apis and firebase triggers in there component
          loadChat();  // Ensure this line is being executed
        }
      });

    return () => {
      unsubscribe();
    };
  }, [userinfo.id]);

  useEffect(() => {
    const unsubscribe = firestore.collection('appointments').where('customerId', '==', `${userinfo.id}`).onSnapshot(() => {
      if (pathname === "/search-business-ai") {
        loadNotifications() // load appointments notifications only because chatclb homepage has its own getAppoinments snapshot
      } else {
        loadAppointments(); // this refreshes the chatslb route when appointments update
      }
    });
    return () => unsubscribe();

  }, [userinfo.id, refetchAppointments, pathname]);

  useEffect(() => {

    const unsubscribe = firestore.collection('comments').where('customerId', '==', `${userinfo.id}`).onSnapshot(() => {
      pathname !== "/search-business-ai" && loadAppointments(); // this refreshes the chatslb route when appointments update
    });
    return () => unsubscribe();

  }, [userinfo.id, refetchAppointments, pathname]);


  useEffect(() => {
    const unsubscribe = firestore.collection('ratings').where('customerId', '==', `${userinfo.id}`).onSnapshot(() => {
      if (!String(pathname).includes('/business')) {

        loadNotifications()
      }
    });
    return () => unsubscribe();
  }, [userinfo.id]);

  // Orders snapshot: refresh the active orders count when the user's orders change
  useEffect(() => {
    if (!userinfo?.id) return;

    const unsubscribe = firestore.collection('orders')
      .where('customerId', '==', `${userinfo.id}`)
      .onSnapshot(async () => {
        getOrders({
          variables: {
            filterInput: {
              skip: 0,
              take: 1000,
              searchText: '',
              status: '',
              source: '',
              startDate: null,
              endDate: null,
              orderView: 'ACTIVE',
            },
          },
        });
      });

    return () => unsubscribe();
  }, [userinfo.id, businessId]);

  // set the google recaptcha api to the golbal state and fetch when required
  useEffect(() => {
    if (googleRecaptchaData) {
      dispatch(updateGoogleRecaptchaApi(googleRecaptchaData?.getGoogleRecaptchaPublicKey));
    }
  }, [googleRecaptchaData]);

  // Set tinyMceKey to the global state
  useEffect(() => {
    if (tinyMceKey) {
      dispatch(setTinyMceKey(tinyMceKey))
    }
  }, [tinyMceKey])

  const isMobile = useIsMobile();

  return (
    <Fragment>
      <section
        className={`page-navbar ${pathname === "/search-business-ai" ? "p-0 m-0" : "section px-1 py-2"}`}>
        <div className={`${pathname === "/search-business-ai" ? "" : "bscontainer p-0"}`}>
          <div className='row m-0'>
            <div className='col-lg-12 p-0'>
              <nav
                className="p-0 navbar box" role='navigation' aria-label='main navigation'>
                <div className='navbar-brand' style={{ width: '100%' }}>
                  {
                    <Link to='/' className='chatslb-logo is-flex' onClick={handleMenuClose}  >
                      <img id="chatclb_icon" src='images/chatclb_logo.png' alt='logo' />
                      <img id="chatclb_text" src='images/logo.png' alt='logo' />
                    </Link>
                  }

                  {authinfo ? (
                    // Desktop navbar buttons after logging in
                    <>
                      <NavLink className='navbar-item px-2 is-hidden-mobile' to='/calender' title='Appointments'
                        style={{ marginLeft: 'auto' }}>
                        <Button size='small' className='button-icon'> <span className='has-text-weight-bold'>  <BsCalendar2Date fontSize={18} /> </span>  </Button>
                      </NavLink>

                      <NavLink className='navbar-item px-2 is-hidden-mobile' to='/business/search' title='Search'>
                        <Button size='small' className='button-icon'>  <Icon name='search' />  </Button>
                      </NavLink>

                      <NavLink to='/favorites' className='navbar-item px-2' title='Favorites'
                        style={{ marginLeft: isMobile && 'auto' }}>
                        <Button size='small' className='button-icon'> <Icon name='heart' />   </Button>
                      </NavLink>

                      <NavLink to='/messages' className='navbar-item px-2' title='Messages'>
                        <Button size='small' className='button-icon'> <Icon name='comments' />  </Button>
                        {userinfo.unreadCount > 0 && (<span className='notification-badge'>  {userinfo.unreadCount > 9 ? '9+' : userinfo.unreadCount}  </span>)}
                      </NavLink>

                      <NavLink to='#' className='navbar-item px-2' title='Notifications'>
                        <Button size='small' className='button-icon' onClick={handleMarkAsViewed}  >  <Icon name='bell' /> </Button>
                        {userinfo.totalNotifications > 0 && (<span className='notification-badge'>  {userinfo.totalNotifications > 9 ? '9+' : userinfo.totalNotifications} </span>)}
                      </NavLink>

                      {!isMobile ?
                        <ProfileDropdown>
                          {/* Profile icon for dropdown */}
                          <NavLink className='navbar-item px-2' to="#" title='Profile'>
                            <Button size='small' light color='info' className='avatar-icon rounded'>
                              <strong>{getAvatarName(userinfo)}</strong>
                            </Button>
                            {!userinfo.isPhoneNumberConfirmed && <span className='profile-badge'>!</span>}
                          </NavLink>

                          {/* dropdown section */}
                          <DropdownContent>
                            <DropdownItem>
                              <div className='is-flex is-align-items-center is-justify-content-center' onClick={() => push('/account/setting')}>
                                <MdOutlineManageAccounts fontSize={16} color={pathname === "/account/setting" ? "#3f9ade" : "black"} className='mr-3' />
                                <span style={{ color: pathname === "/account/setting" ? "#3f9ade" : "black" }}> Manage Account </span>
                              </div>
                            </DropdownItem>
                            <DropdownItem className='is-flex is-justify-content-space-between' onClick={() => push('/orders')}>
                              <div className='is-flex is-align-items-center is-justify-content-center'>
                                <AiOutlineShoppingCart fontSize={16} color={pathname === "/orders" ? "#3f9ade" : "black"} className='mr-3' />
                                <span style={{ color: pathname === "/orders" ? "#3f9ade" : "black" }}> Orders </span>
                              </div>
                              {isActiveOrdersLoading ? <ClipLoader className='mr-2' size={10} /> : activeOrdersCount > 0 && <OrdersBadge>  {activeOrdersCount || 0} active</OrdersBadge>}

                            </DropdownItem>
                            {authinfo &&
                              <DropdownItem onClick={handleLogout}>
                                <div className='is-flex is-align-items-center is-justify-content-center'>
                                  <GrLogout fontSize={16} color='red' className='mr-3' style={{ marginLeft: '2px' }} />
                                  <span style={{ marginLeft: '-2px' }}> Logout </span>
                                </div>
                              </DropdownItem>}
                            <hr className='my-3' />
                            <p className='is-size-7 has-text-grey has-text-centered'>chatCLB 2025. All rights reserved. <br /> Terms and Conditions | Privacy Policy <br /> Version: 11.6.2</p>
                          </DropdownContent>
                        </ProfileDropdown> : (
                          authinfo &&
                          <NavLink to="/account/setting" className='navbar-item px-2' title='Profile' >
                            <Button size='small' light color='info' className='avatar-icon rounded'>  <strong> {getAvatarName(userinfo)}</strong> </Button>
                            {!userinfo.isPhoneNumberConfirmed && <span className='profile-badge'>!</span>}
                          </NavLink>
                        )}
                    </>
                  ) : (
                    // Desktop navbar buttons before logging in
                    <>
                      <div className='navbar-item p-0 mr-2' style={{ marginLeft: 'auto' }}>
                        <div className='buttons'>
                          <NavLink className='navbar-item px-2 is-hidden-mobile' to='/business/search' title='Search'>
                            <Button size='small' className='button-icon'>  <Icon name='search' />  </Button>
                          </NavLink>
                          <button className='button is-small' onClick={attemptRegister}  > Sign Up </button>
                          <button className='button is-hidden-mobile is-outlined is-light is-info mr-3 is-small' onClick={attemptLogin}  >  Sign In  </button>
                          <button className='button is-hidden-tablet is-outlined is-light is-info is-small' onClick={attemptLogin}  >  Sign In  </button>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Mobile navbar hamburger */}
                  <span onClick={handleMenuClick} role='button' className={`navbar-burger is-hidden-tablet burger m-0 ${isActive && 'is-active'}`} aria-label='menu' aria-expanded='false' data-target='mainNavbar' >
                    <span aria-hidden='true' />
                    <span aria-hidden='true' />
                    <span aria-hidden='true' />
                  </span>
                </div>

                {/* Mobile navbar dropdown */}
                <div className={'navbar-menu is-hidden-tablet' + (isActive ? ' is-active' : '')}  >
                  <div className='navbar-end'>
                    <a href={`${APIPaths.WebAdminFrontend}`} className='navbar-item'>    Admin Login   </a>
                    <Link to='about-us' className='navbar-item' onClick={handleMenuClose} >  About Us  </Link>
                    <Link to='business-pricing' className='navbar-item' onClick={handleMenuClose}> Business Pricing  </Link>
                    {authinfo && <Link to="/calender" className="navbar-item" onClick={handleMenuClose}> Appointments </Link>}
                    {authinfo && <Link to="/orders" className="navbar-item is-flex is-justify-content-space-between" onClick={handleMenuClose}>
                      <span>Orders</span>
                      {activeOrdersCount > 0 && <OrdersBadge>  {activeOrdersCount || 0} active</OrdersBadge>}
                    </Link>}
                    <Link to="/search" className="navbar-item" onClick={handleMenuClose}> Search </Link>
                    <Link to="/contact-us" className="navbar-item" onClick={handleMenuClose}> Contact Us </Link>
                    <Link to="/terms-and-conditions" className="navbar-item" onClick={handleMenuClose}> Terms and Conditions </Link>
                    <Link to="/privacy-policy" className="navbar-item" onClick={handleMenuClose}> Privacy Policy </Link>
                    {authinfo && <NavLink tag={Link} className={`navbar-item`} to='/' onClick={handleLogout} >  Logout </NavLink>}
                  </div>
                </div>
              </nav>
            </div>
          </div>

        </div >
      </section >
    </Fragment >
  )
}

export default NavMenu